import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { uploadRows } from "../../../store/actions/councilActions/bulkUploadAction";
import { getTrades } from "../../../store/actions/councilActions/trade";
import {
  getCourses,
  getSubjects,
} from "../../../store/actions/councilActions/courseAction";
import { exportToCSV } from "../../../utils/exportCsv";
import { readSheet } from "../../../utils/readSheet";
import { errorAlert } from "../../../utils/alert";

/*
 * Admin: upload questions and marks from a spreadsheet.
 *
 * Nothing is written until the council has seen what the file contains. The
 * file is read in the browser, checked against the server without saving, and
 * only then imported - so a wrong column or a bad roll number is caught before
 * it becomes 400 rows of nonsense in the database.
 */

const TABS = [
  {
    key: "questions",
    label: "Questions",
    blurb:
      "Adds questions to the question bank. Choose the subject below, then upload the questions for it. The correct option is the number of the option that is right.",
    columns: [
      "question",
      "option_1",
      "option_2",
      "option_3",
      "option_4",
      "correct_option",
    ],
    sample: {
      question: "Which is the longest bone in the human body?",
      option_1: "Femur",
      option_2: "Tibia",
      option_3: "Humerus",
      option_4: "Radius",
      correct_option: 1,
    },
  },
  {
    key: "assignment",
    label: "Assignment Marks",
    blurb:
      "Assignment marks, one row per student per subject. Uploading the same student and subject again corrects the mark rather than adding a second one.",
    columns: ["roll_number", "subject_code", "marks"],
    sample: { roll_number: "2026070054", subject_code: "OT-101", marks: 42 },
  },
  {
    key: "result",
    label: "Result Marks",
    blurb:
      "Exam marks, one row per student per subject. The semester is taken from the subject, so a semester course files them under the right term.",
    columns: ["roll_number", "subject_code", "marks"],
    sample: { roll_number: "2026070054", subject_code: "OT-101", marks: 47 },
  },
];

const BulkUpload = () => {
  const dispatch = useDispatch();

  const [tab, setTab] = useState("questions");

  // Questions go into one subject, chosen here rather than named in the file.
  const [trades, setTrades] = useState([]);
  const [subTrades, setSubTrades] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [picked, setPicked] = useState({
    trade: "",
    subTrade: "",
    course: "",
    subject: "",
  });

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const active = TABS.find((t) => t.key === tab);
  const needsSubject = tab === "questions";

  useEffect(() => {
    dispatch(getTrades()).then((list) => setTrades(list || []));
  }, [dispatch]);

  /* Each box refills the one after it and empties the rest, so a half
     changed choice can never be left pointing at the wrong subject. */
  const pickTrade = (id) => {
    const trade = trades.find((t) => String(t.id) === String(id));
    setPicked({ trade: id, subTrade: "", course: "", subject: "" });
    setSubTrades(trade?.subtrade || []);
    setCourses([]);
    setSubjects([]);
  };

  const pickSubTrade = async (id) => {
    setPicked((p) => ({ ...p, subTrade: id, course: "", subject: "" }));
    setCourses([]);
    setSubjects([]);
    if (id) setCourses((await dispatch(getCourses(id))) || []);
  };

  const pickCourse = async (id) => {
    setPicked((p) => ({ ...p, course: id, subject: "" }));
    setSubjects([]);
    if (id) setSubjects((await dispatch(getSubjects(id))) || []);
  };

  const reset = () => {
    setFileName("");
    setRows([]);
    setCheck(null);
    setDone(null);
  };

  const switchTab = (key) => {
    setTab(key);
    reset();
  };

  const downloadTemplate = () => {
    // One filled-in row, so the header names and the shape are both obvious.
    exportToCSV([active.sample], `${active.key}-template`);
  };

  /* What the screen contributes on top of the file. */
  const extra = () =>
    needsSubject && picked.subject ? { subject_id: picked.subject } : {};

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (needsSubject && !picked.subject) {
      errorAlert("Please choose the subject first, then pick the file.");
      e.target.value = "";
      return;
    }

    reset();
    setFileName(file.name);
    setBusy(true);

    try {
      const { headers, rows: parsed } = await readSheet(file);

      if (parsed.length === 0) {
        errorAlert("That file has no rows under the heading line.");
        setBusy(false);
        return;
      }

      const missing = active.columns
        .filter((c) => !["option_3", "option_4"].includes(c))
        .filter((c) => !headers.includes(c));

      if (missing.length) {
        errorAlert(`The file is missing these columns: ${missing.join(", ")}`);
        setBusy(false);
        return;
      }

      setRows(parsed);

      // Ask the server what it makes of the rows, without saving any of them.
      const result = await dispatch(uploadRows(tab, parsed, true, extra()));
      setCheck(result || null);
    } catch (err) {
      errorAlert("That file could not be read. Please save it as .xlsx or .csv.");
    }

    setBusy(false);
  };

  const doImport = async () => {
    setBusy(true);
    const result = await dispatch(uploadRows(tab, rows, false, extra()));
    setDone(result || null);
    setBusy(false);
  };

  const preview = rows.slice(0, 8);
  const previewCols = preview.length ? Object.keys(preview[0]) : [];

  return (
    <div>
      <div class="BigTitle">Bulk Upload</div>

      <ul class="nav nav-tabs mt-3">
        {TABS.map((t) => (
          <li class="nav-item" key={t.key}>
            <button
              type="button"
              class={`nav-link ${tab === t.key ? "active" : ""}`}
              onClick={() => switchTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div class="card p-3 mt-3">
        <p class="mb-2">{active.blurb}</p>
        <p class="mb-2">
          <b>Columns:</b> {active.columns.join(", ")}
          {tab === "questions" ? " (option_3 and option_4 are optional)" : ""}
        </p>

        {/* Questions belong to one subject, and a subject code on its own is
            ambiguous - the same code exists under several courses. Choosing
            the way down to it removes the guess. */}
        {needsSubject && (
          <div class="row">
            <div class="col-md-3 mb-2">
              <label>Trade *</label>
              <select
                class="form-control"
                value={picked.trade}
                onChange={(e) => pickTrade(e.target.value)}
              >
                <option value="">-- select --</option>
                {trades.map((t) => (
                  <option value={t.id} key={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-3 mb-2">
              <label>Sub Trade *</label>
              <select
                class="form-control"
                value={picked.subTrade}
                disabled={!picked.trade}
                onChange={(e) => pickSubTrade(e.target.value)}
              >
                <option value="">-- select --</option>
                {subTrades.map((t) => (
                  <option value={t.id} key={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-3 mb-2">
              <label>Course *</label>
              <select
                class="form-control"
                value={picked.course}
                disabled={!picked.subTrade}
                onChange={(e) => pickCourse(e.target.value)}
              >
                <option value="">-- select --</option>
                {courses.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-3 mb-2">
              <label>Subject *</label>
              <select
                class="form-control"
                value={picked.subject}
                disabled={!picked.course}
                onChange={(e) => {
                  setPicked((prev) => ({ ...prev, subject: e.target.value }));
                  // A different subject makes the file already read against
                  // the old one meaningless, so it is cleared.
                  reset();
                }}
              >
                <option value="">-- select --</option>
                {subjects.map((sub) => (
                  <option value={sub.id} key={sub.id}>
                    {sub.name}
                    {sub.code ? ` (${sub.code})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div class="row align-items-end">
          <div class="col-md-5 mb-2">
            <label>Excel or CSV file</label>
            <input
              type="file"
              class="form-control"
              accept=".xlsx,.xls,.csv"
              onChange={onFile}
              disabled={busy || (needsSubject && !picked.subject)}
            />
          </div>
          <div class="col-md-3 mb-2">
            <button
              type="button"
              class="btn btn-light-primary btn-block"
              onClick={downloadTemplate}
            >
              Download template
            </button>
          </div>
          {fileName && (
            <div class="col-md-4 mb-2 text-muted">
              {fileName} - {rows.length} row(s) read
            </div>
          )}
        </div>
      </div>

      {/* ---- what the server says will happen ---- */}
      {check && (
        <div class="card p-3 mt-3">
          <h6 class="mb-2">Check</h6>
          <p class="mb-2">
            <span class="text-success">
              <b>{check.imported}</b> row(s) will be imported
            </span>
            {check.skipped?.length ? (
              <span class="text-danger">
                {" "}
                &nbsp;|&nbsp; <b>{check.skipped.length}</b> will be skipped
              </span>
            ) : null}
          </p>

          {check.skipped?.length > 0 && (
            <div class="table-responsive" style={{ maxHeight: "220px" }}>
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th style={{ width: "90px" }}>File row</th>
                    <th>Why it is skipped</th>
                  </tr>
                </thead>
                <tbody>
                  {check.skipped.map((s, i) => (
                    <tr key={i}>
                      <td>{s.row}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div class="mt-2">
            <button
              type="button"
              class="btn btn-comman"
              disabled={busy || !check.imported || !!done}
              onClick={doImport}
            >
              {busy
                ? "Working..."
                : `Import ${check.imported} row(s)`}
            </button>
          </div>
        </div>
      )}

      {done && (
        <div class="alert alert-success mt-3">
          <b>{done.imported}</b> row(s) imported
          {done.skipped?.length ? `, ${done.skipped.length} skipped.` : "."}
        </div>
      )}

      {/* ---- the file as it was read ---- */}
      {preview.length > 0 && (
        <div class="card p-3 mt-3">
          <h6 class="mb-2">
            First {preview.length} row(s) as read from the file
          </h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  {previewCols.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    {previewCols.map((c) => (
                      <td key={c}>{r[c]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
